// Temporary variable to keep track of routes that are migrated to tanstack router
import type { LDNDBlock } from '@/features/AssetDetailView/FolderDetailView/C006AirframeADs/types';
import { FolderConfig, FolderLetter } from '@/features/end-of-lease/types';

export const USER_ACTIVITY_EVENTS = [
    'mousemove',
    'mousedown',
    'keydown',
    'touchstart',
    'scroll',
];

export const TIMEOUT_INACTIVITY_MINUTES = 30;
export const REFRESH_TOKEN_THRESHOLD_MINUTES = 2;
export const REFRESH_TOKEN_INTERVAL_CHECK_MS = 60000; // 1 minute

// File types supported in end-of-lease projects
export const END_OF_LEASE_FILE_TYPES = [
    'eo', // Engineering Order
    'sb', // Service Bulletin
    'sbsr', // Service Bulletin Status Report
    'air', // Aircraft Inspection Report
    'mpd', // Maintenance Planning Document
    'ad', // Airworthiness Directives
    'adsr', // Airworthiness Directive Status Report
    'wo', // Work Order
    'tc', // Task Card
    'dir', // Discrepancy Report
    'ril', // Removal/Installation List
    'ct', // Component Tag
    'rdas', // Repair Design Approval Sheet
    'ur', // Utilization Report
    'ldnd', // Last Done / Next Due
    'adtlb', // Aircraft Delivery Technical Logbook
    'sv', // Shop Visit Report
    'amp', // Aircraft Maintenance Program
] as const;

// Extended list including additional ingestion types for document upload
export const ALL_INGESTION_TYPES = [
    'eo', // Engineering Order
    'sb', // Service Bulletin
    'sbsr', // Service Bulletin Status Report
    'air', // Aircraft Inspection Report
    'mpd', // Maintenance Planning Document
    'ad', // Airworthiness Directives
    'adsr', // Airworthiness Directive Status Report
    'sr', // status report
    'wo', // Work Order
    'tc', // Task Card
    'dir', // Discrepancy Report
    'ril', // Removal/Installation List
    'ct', // Component Tag
    'rdas', // Repair Design Approval Sheet
    'ur', // Utilization Report
    'ldnd', // Last Done / Next Due
    'adtlb', // Aircraft Delivery Technical Logbook
    'sv', // Shop Visit Report
    'amp', // Aircraft Maintenance Program
    'lptr', // Life Preserver Teardown Report
] as const;

// End-of-lease file type options with labels for UI dropdowns and selects
// File type color mappings for UI badges and tags
export const FILE_TYPE_COLORS = {
    sb: 'bg-blue-100 text-blue-800 border-blue-200',
    sbsr: 'bg-blue-100 text-blue-800 border-blue-200',
    ad: 'bg-orange-100 text-orange-800 border-orange-200',
    adsr: 'bg-orange-100 text-orange-800 border-orange-200',
    eo: 'bg-purple-100 text-purple-800 border-purple-200',
    dr: 'bg-orange-100 text-orange-800 border-orange-200',
    sr: 'bg-teal-100 text-teal-800 border-teal-200',
    arc: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    lptr: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    air: 'bg-violet-100 text-violet-800 border-violet-200',
    arl: 'bg-violet-100 text-violet-800 border-violet-200',
    asl: 'bg-violet-100 text-violet-800 border-violet-200',
    oem: 'bg-violet-100 text-violet-800 border-violet-200',
    mpd: 'bg-rose-100 text-rose-800 border-rose-200',
    contract: 'bg-green-100 text-green-800 border-green-200',
    cmm: 'bg-pink-100 text-pink-800 border-pink-200',
    cmr: 'bg-pink-100 text-pink-800 border-pink-200',
    wo: 'bg-amber-100 text-amber-800 border-amber-200',
    wr: 'bg-amber-100 text-amber-800 border-amber-200',
    nrms: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    nrjc: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    ut: 'bg-gray-100 text-gray-800 border-gray-200',
    tc: 'bg-lime-100 text-lime-800 border-lime-200',
    tls: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    ajl: 'bg-stone-100 text-stone-800 border-stone-200',
    dfp: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    dir: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    ril: 'bg-sky-100 text-sky-800 border-sky-200',
    ct: 'bg-sky-100 text-sky-800 border-sky-200',
    pmc: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    rdas: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200',
    ur: 'bg-teal-100 text-teal-800 border-teal-200',
    ldnd: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    adtlb: 'bg-stone-100 text-stone-800 border-stone-200',
    sv: 'bg-amber-100 text-amber-800 border-amber-200',
    afm: 'bg-sky-100 text-sky-800 border-sky-200',
    amp: 'bg-indigo-100 text-indigo-800 border-indigo-200',
} as const;

export const DEFAULT_FILE_TYPE_COLOR =
    'bg-gray-100 text-gray-800 border-gray-200';

// File FORMAT badge colours (keyed by lowercase extension, distinct from document-type colours above).
// PRD §5.2 defines these for the bulk-upload selected-files tree.
export const FILE_FORMAT_COLORS: Record<string, string> = {
    csv: 'bg-lime-100 text-lime-700',
    xlsx: 'bg-[#33C481]/25 text-[#185C37]',
    xls: 'bg-[#33C481]/25 text-[#185C37]',
    pdf: 'bg-[#FF9999]/25 text-[#990000]',
    jpg: 'bg-orange-100 text-orange-700',
    jpeg: 'bg-orange-100 text-orange-700',
    png: 'bg-violet-100 text-violet-700',
    tiff: 'bg-cyan-100 text-cyan-700',
    tif: 'bg-cyan-100 text-cyan-700',
    // ZIP: no badge colour
};

// Document Repository tree view (EOL-1703) shared class-name constants.
// Column widths MUST stay identical across the tree header and all three row
// components (FolderRow / TreeFileRow / TreeDocumentRow) so the columns align.
export const TREE_ROW = 'flex items-center gap-1 px-2';
export const TREE_COLUMNS = {
    checkbox: 'w-5 shrink-0 flex items-center',
    icon: 'w-5 shrink-0 flex items-center justify-center',
    caret: 'w-5 shrink-0 flex items-center justify-center',
    name: 'flex-[2] min-w-0 flex items-center gap-2',
    docType: 'w-[120px] shrink-0 flex items-center',
    size: 'w-[88px] shrink-0 text-right',
    filePath: 'flex-[2] min-w-0 pl-3',
    status: 'w-[104px] shrink-0 flex items-center',
    action: 'w-8 shrink-0 flex items-center justify-center',
} as const;
// Name cell spans the name + doc-type columns; keep the 120px in sync with docType above.
export const TREE_NAME_SPANNING_DOCTYPE = 'flex-[2_1_120px]';
export const TREE_ROW_ESTIMATE_PX = 42;
export const TREE_OVERSCAN = 20;

export const TREE_INDENT_STEP_PX = 8;
export const TREE_ROW_SELECTED_BG = 'bg-gray-100 hover:bg-gray-200';
export const TREE_ROW_HOVER_BG = 'hover:bg-slate-50';
export const TREE_FOLDER_BG = 'bg-[#eff9f9] hover:bg-[#e2f0f0]';
export const TREE_ROW_ERROR_BG = 'bg-red-50/50 hover:bg-red-50/70';
// Inset so it never shifts layout, faint teal so it reads as focus without
// competing with the filled selected-row background.
export const TREE_ROW_FOCUS_RING = 'ring-1 ring-inset ring-[#476969]/40';

// Placeholder error copy for client-tracked upload failures (EOL-1570 Decision 4).
// A proper backend error field on the poll response is a follow-up task.
export const UPLOAD_ERROR_COPY = {
    serverTimeout: 'Try again later as the server timed out',
    corruptedFile: 'Unable to upload corrupted folders or files',
    ingestionFailed: 'Failed to process this file',
} as const;

// HARDCODED: IATA Technical Binder Display Configuration
// TODO: Consider making assignees and priorities configurable rather than hardcoded
export const IATA_BINDER_DISPLAY_MAP = {
    Certificates: {
        letter: 'A',
        name: 'Certificates',
        assignee: 'CAMO Team',
        priority: 'high' as const,
    },
    MaintenanceStatusSummaries: {
        letter: 'B',
        name: 'Maintenance Status Summaries',
        assignee: 'MRO Team',
        priority: 'high' as const,
    },
    AircraftMaintenanceRecords: {
        letter: 'C',
        name: 'Aircraft Maintenance Records',
        assignee: 'CAMO Team',
        priority: 'high' as const,
    },
    ConfigurationStatus: {
        letter: 'D',
        name: 'Configuration Status',
        assignee: 'Engineering',
        priority: 'medium' as const,
    },
    AircraftManufacturerRecords: {
        letter: 'E',
        name: 'Aircraft Manufacturer Records',
        assignee: 'MRO Team',
        priority: 'medium' as const,
    },
    EngineRecords_1: {
        letter: 'F1',
        name: 'Engine Records 1',
        assignee: 'Engine Shop',
        priority: 'high' as const,
    },
    EngineRecords_2: {
        letter: 'F2',
        name: 'Engine Records 2',
        assignee: 'Engine Shop',
        priority: 'high' as const,
    },
    APURecords: {
        letter: 'G',
        name: 'APU Records',
        assignee: 'APU Shop',
        priority: 'medium' as const,
    },
    ComponentRecords: {
        letter: 'H',
        name: 'Component Records',
        assignee: 'Planning',
        priority: 'high' as const,
    },
    LandingGearRecords: {
        letter: 'I',
        name: 'Landing Gear Records',
        assignee: 'LG Shop',
        priority: 'medium' as const,
    },
    Manuals: {
        letter: 'J',
        name: 'Manuals',
        assignee: 'Documentation',
        priority: 'low' as const,
    },
    Miscellaneous: {
        letter: 'K',
        name: 'Miscellaneous',
        assignee: 'General',
        priority: 'low' as const,
    },
} as const;

// Project task select options for forms and filters
export const PROJECT_TASK_PARTIES = [
    { value: 'lessor', label: 'Lessor' },
    { value: 'camo', label: 'CAMO' },
    { value: 'mro', label: 'MRO' },
];

export const PROJECT_TASK_PRIORITIES = [
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' },
];

export const PROJECT_TASK_STATUSES = [
    { value: 'not_started', label: 'Not Started' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'on_hold', label: 'On Hold' },
    { value: 'completed', label: 'Completed' },
];

export const COMPLETE_STATUS_COLOR = '#22c55e';
export const IN_PROGRESS_STATUS_COLOR = '#60a5fa';
export const ON_HOLD_STATUS_COLOR = '#f97316';
export const NOT_STARTED_STATUS_COLOR = '#cbd5e1';

// TODO: Add more configurations for other folders!
export const FOLDER_CONFIGURATIONS: Record<FolderLetter, FolderConfig> = {
    B: {
        name: 'Maintenance Status Summaries',
        description:
            'Maintenance status summaries and airworthiness directive compliance records',
        types: [
            {
                id: 1,
                title: 'AD Status (Airframe)',
                description: 'Airworthiness Directive status for airframe',
                contentType: 'none',
                apiCategory: 'B001',
            },
            {
                id: 2,
                title: 'AD Status (Appliance)',
                description: 'Airworthiness Directive status for appliances',
                contentType: 'none',
                apiCategory: 'B002',
            },
            {
                id: 3,
                title: 'AD Status (Engine)',
                description: 'Airworthiness Directive status for engines',
                contentType: 'none',
                apiCategory: 'B003',
            },
            {
                id: 4,
                title: 'Certified status of Airframe & Appliance Airworthiness Directives',
                description:
                    'Certified status of Airframe & Appliance Airworthiness Directives',
                contentType: 'ad-demo',
                apiCategory: 'B004',
            },
            {
                id: 5,
                title: 'Service Bulletin Status (Airframe)',
                description: 'Service Bulletin status for airframe',
                contentType: 'none',
                apiCategory: 'B005',
            },
            {
                id: 6,
                title: 'Service Bulletin Status (Engine)',
                description: 'Service Bulletin status for engines',
                contentType: 'none',
                apiCategory: 'B006',
            },
            {
                id: 7,
                title: 'Service Bulletin Status (APU)',
                description: 'Service Bulletin status for APU',
                contentType: 'none',
                apiCategory: 'B007',
            },
            {
                id: 8,
                title: 'Service Bulletin Status (Landing Gear)',
                description: 'Service Bulletin status for landing gear',
                contentType: 'none',
                apiCategory: 'B008',
            },
            {
                id: 9,
                title: 'Service Bulletin Status (Components)',
                description: 'Service Bulletin status for components',
                contentType: 'none',
                apiCategory: 'B009',
            },
            {
                id: 10,
                title: 'Modification Status (Airframe)',
                description: 'Modification status for airframe',
                contentType: 'none',
                apiCategory: 'B010',
            },
            {
                id: 11,
                title: 'Modification Status (Engine)',
                description: 'Modification status for engines',
                contentType: 'none',
                apiCategory: 'B011',
            },
            {
                id: 12,
                title: 'Repair Status (Airframe)',
                description: 'Repair status for airframe',
                contentType: 'none',
                apiCategory: 'B012',
            },
            {
                id: 13,
                title: 'Hard Time Component Status',
                description: 'Hard Time Component maintenance status',
                contentType: 'none',
                apiCategory: 'B013',
            },
            {
                id: 14,
                title: 'Life Limited Parts Status (Airframe)',
                description: 'Life Limited Parts status for airframe',
                contentType: 'none',
                apiCategory: 'B014',
            },
            {
                id: 15,
                title: 'Life Limited Parts Status (Engine)',
                description: 'Life Limited Parts status for engines',
                contentType: 'none',
                apiCategory: 'B015',
            },
            {
                id: 16,
                title: 'Life Limited Parts Status (APU)',
                description: 'Life Limited Parts status for APU',
                contentType: 'none',
                apiCategory: 'B016',
            },
            {
                id: 17,
                title: 'Life Limited Parts Status (Landing Gear)',
                description: 'Life Limited Parts status for landing gear',
                contentType: 'none',
                apiCategory: 'B017',
            },
            {
                id: 18,
                title: 'Oil and Fluid Types Statement',
                description:
                    'Certified statement of Oil and Fluid types used in Aircraft, Engines and APU',
                contentType: 'none',
                apiCategory: 'B018',
            },
            {
                id: 19,
                title: 'On-Condition Components Status',
                description: 'On-Condition Components maintenance status',
                contentType: 'none',
                apiCategory: 'B019',
            },
            {
                id: 20,
                title: 'CMR/ETOPS Status',
                description:
                    'Certification Maintenance Requirements / ETOPS status',
                contentType: 'none',
                apiCategory: 'B020',
            },
            {
                id: 21,
                title: 'CPCP Status',
                description: 'Corrosion Prevention and Control Program status',
                contentType: 'none',
                apiCategory: 'B021',
            },
            {
                id: 22,
                title: 'Fuel Tank Safety Status',
                description: 'Fuel Tank Safety compliance status',
                contentType: 'none',
                apiCategory: 'B022',
            },
            {
                id: 23,
                title: 'SSIP/MSG-3 Status',
                description:
                    'Structural Significant Item Program / MSG-3 status',
                contentType: 'none',
                apiCategory: 'B023',
            },
            {
                id: 24,
                title: 'Aging Aircraft Status',
                description: 'Aging Aircraft compliance status',
                contentType: 'none',
                apiCategory: 'B024',
            },
            {
                id: 25,
                title: 'EWIS Status',
                description: 'Electrical Wiring Interconnection System status',
                contentType: 'none',
                apiCategory: 'B025',
            },
            {
                id: 26,
                title: 'Aircraft Utilization Status',
                description: 'Aircraft utilization and hours/cycles status',
                contentType: 'none',
                apiCategory: 'B026',
            },
            {
                id: 27,
                title: 'Engine Utilization Status',
                description: 'Engine utilization and hours/cycles status',
                contentType: 'none',
                apiCategory: 'B027',
            },
            {
                id: 28,
                title: 'APU Utilization Status',
                description: 'APU utilization and hours/cycles status',
                contentType: 'none',
                apiCategory: 'B028',
            },
            {
                id: 29,
                title: 'Landing Gear Utilization Status',
                description: 'Landing Gear utilization and hours/cycles status',
                contentType: 'none',
                apiCategory: 'B029',
            },
            {
                id: 30,
                title: 'Deferred Defects Status',
                description: 'Deferred Defects maintenance status',
                contentType: 'none',
                apiCategory: 'B030',
            },
            {
                id: 31,
                title: 'MEL/CDL Status',
                description:
                    'Minimum Equipment List / Configuration Deviation List status',
                contentType: 'none',
                apiCategory: 'B031',
            },
            {
                id: 32,
                title: 'Reliability Reports',
                description: 'Aircraft reliability reports and statistics',
                contentType: 'none',
                apiCategory: 'B032',
            },
            {
                id: 33,
                title: 'Maintenance Program Status',
                description: 'Maintenance Program compliance status',
                contentType: 'none',
                apiCategory: 'B033',
            },
        ],
    },
    C: {
        name: 'Aircraft Maintenance Records',
        description:
            'Airworthiness Directives and Service Bulletins compliance records',
        types: [
            {
                id: 1,
                title: 'CRS/Release to service statements',
                description: 'Certificate of Release to Service statements',
                contentType: 'none',
                apiCategory: 'C001',
            },
            {
                id: 2,
                title: 'Last complete cycle of A-Checks',
                description: 'Records of last complete A-Check cycle',
                contentType: 'none',
                apiCategory: 'C002',
            },
            {
                id: 3,
                title: 'Last complete cycle of C-Checks',
                description: 'Records of last complete C-Check cycle',
                contentType: 'none',
                apiCategory: 'C003',
            },
            {
                id: 4,
                title: 'Major structural check packages',
                description: 'Major structural inspection and check packages',
                contentType: 'none',
                apiCategory: 'C004',
            },
            {
                id: 5,
                title: 'CPCP/ISIP Certified maintenance task cards',
                description:
                    'Corrosion Prevention and Control Program / ISIP certified maintenance task cards',
                contentType: 'none',
                apiCategory: 'C005',
            },
            {
                id: 6,
                title: 'AD Compliance files',
                description:
                    'Airworthiness Directives for airframe and appliance components',
                contentType: 'ad-demo',
                apiCategory: 'C006',
            },
            {
                id: 7,
                title: 'Service Bulletins',
                description:
                    'Airframe and vendor Service Bulletins compliance tracking',
                contentType: 'sb-demo',
                apiCategory: 'C007',
            },
            {
                id: 8,
                title: 'Non-OEM Modifications',
                description: 'Non-OEM modification records and documentation',
                contentType: 'none',
                apiCategory: 'C008',
            },
            {
                id: 9,
                title: 'Certified Maintenance for Structural Repairs',
                description:
                    'Certified maintenance records for structural repairs',
                contentType: 'none',
                apiCategory: 'C009',
            },
            {
                id: 10,
                title: 'Aircraft weight report',
                description: 'Aircraft weight and balance reports',
                contentType: 'none',
                apiCategory: 'C010',
            },
            {
                id: 11,
                title: 'Flight Control Balance Status',
                description: 'Flight control surface balance status records',
                contentType: 'none',
                apiCategory: 'C011',
            },
            {
                id: 12,
                title: 'Last demonstration flight report',
                description: 'Last demonstration flight report documentation',
                contentType: 'none',
                apiCategory: 'C012',
            },
            {
                id: 13,
                title: 'Compass Deviation Report',
                description: 'Compass deviation calibration reports',
                contentType: 'none',
                apiCategory: 'C013',
            },
            {
                id: 14,
                title: 'Current Hard Copy Records Inventory',
                description:
                    'Inventory of current hard copy records maintained',
                contentType: 'none',
                apiCategory: 'C014',
            },
        ],
    },
    E: {
        name: 'Aircraft Manufacturer Records',
        description: 'Original aircraft manufacturer delivery and build records',
        types: [
            {
                id: 1,
                title: 'Certificate of Airworthiness or Certificate of Airworthiness for Export at Manufacture',
                description:
                    'Certificate of Airworthiness issued at manufacture, or its export equivalent',
                contentType: 'none',
                apiCategory: 'E001',
            },
            {
                id: 2,
                title: 'OEM report of Airworthiness Directives incorporated at Manufacture',
                description:
                    'Airworthiness Directives embodied by the manufacturer before delivery',
                contentType: 'none',
                apiCategory: 'E002',
            },
            {
                id: 3,
                title: "OEM's original component fitted listing",
                description:
                    'Manufacturer listing of the components fitted to the aircraft at build',
                contentType: 'none',
                apiCategory: 'E003',
            },
            {
                id: 4,
                title: "OEM's Repair/Alteration Report/Significant Repair Log",
                description:
                    'Manufacturer repair, alteration, and significant repair records',
                contentType: 'none',
                apiCategory: 'E004',
            },
            {
                id: 5,
                title: 'OEM report of Modifications incorporated at manufacture',
                description:
                    'Modifications embodied by the manufacturer before delivery',
                contentType: 'none',
                apiCategory: 'E005',
            },
            {
                id: 6,
                title: 'Service Difficulty Reports / Delivery Inspection Report',
                description:
                    'Service difficulty reports and the delivery inspection report',
                contentType: 'none',
                apiCategory: 'E006',
            },
            {
                id: 7,
                title: 'Production Aircraft Test Completion Record',
                description: 'Record of production test completion',
                contentType: 'none',
                apiCategory: 'E007',
            },
            {
                id: 8,
                title: 'OEM Flight Logs',
                description: 'Manufacturer flight logs recorded before delivery',
                contentType: 'none',
                apiCategory: 'E008',
            },
            {
                id: 9,
                title: 'Aircraft Historical / Miscellaneous Log',
                description: 'Aircraft historical and miscellaneous log entries',
                contentType: 'none',
                apiCategory: 'E009',
            },
            {
                id: 10,
                title: 'OEM Report of Landing Gear Life Limited Parts Installed at Manufacture including Part Numbers and Serial Numbers',
                description:
                    'Landing gear life limited parts fitted at manufacture, with part and serial numbers',
                contentType: 'none',
                apiCategory: 'E010',
            },
            {
                id: 11,
                title: 'Statement of the Aircraft eligible Type Certification',
                description: 'Statement of the type certification the aircraft is eligible for',
                contentType: 'none',
                apiCategory: 'E011',
            },
            {
                id: 12,
                title: 'Certified Aircraft Weighing Report at Manufacture',
                description: 'Certified weighing report produced at manufacture',
                contentType: 'none',
                apiCategory: 'E012',
            },
            {
                id: 13,
                title: 'Certificate of Conformance',
                description: 'Manufacturer certificate of conformance',
                contentType: 'none',
                apiCategory: 'E013',
            },
            {
                id: 14,
                title: 'Production Flight Certificate',
                description: 'Certificate issued after the production flight',
                contentType: 'none',
                apiCategory: 'E014',
            },
            {
                id: 15,
                title: 'Final Inspection Report',
                description: 'Final inspection report before delivery',
                contentType: 'none',
                apiCategory: 'E015',
            },
            {
                id: 16,
                title: 'Rigging Brochure',
                description: 'Aircraft rigging brochure',
                contentType: 'none',
                apiCategory: 'E016',
            },
            {
                id: 17,
                title: 'Certificate of Sanitary Construction',
                description: 'Certificate of sanitary construction',
                contentType: 'none',
                apiCategory: 'E017',
            },
            {
                id: 18,
                title: 'Detailed Specification / Technical Description Document',
                description:
                    'Detailed specification and technical description of the aircraft',
                contentType: 'none',
                apiCategory: 'E018',
            },
            {
                id: 19,
                title: 'Interior Finish Specification Document',
                description: 'Specification of the aircraft interior finish',
                contentType: 'none',
                apiCategory: 'E019',
            },
            {
                id: 20,
                title: 'Customer Checklist Document',
                description: 'Customer acceptance checklist',
                contentType: 'none',
                apiCategory: 'E020',
            },
        ],
    },
    H: {
        name: 'Component Records',
        description: 'Aircraft component maintenance and documentation',
        types: [
            {
                id: 1,
                title: 'Hard Time Control Components',
                description:
                    'Components with time-based maintenance requirements',
                contentType: 'ht-demo',
                apiCategory: 'H001',
            },
            {
                id: 2,
                title: 'On-Condition / Condition-Monitored Components',
                description:
                    'Components monitored based on condition and usage',
                contentType: 'oc-demo',
                apiCategory: 'H002',
            },
        ],
    },
    F1: {
        name: 'Engine Records 1',
        description:
            'Engine life limited parts, maintenance records, and back-to-birth traceability',
        types: [
            {
                id: 1,
                title: 'Life Limited Parts',
                description:
                    'Engine Life Limited Parts tracking with cycle limits, remaining life, and back-to-birth documentation',
                contentType: 'llp-demo',
                apiCategory: 'F008',
            },
        ],
    },
    F2: {
        name: 'Engine Records 2',
        description:
            'Engine life limited parts, maintenance records, and back-to-birth traceability for engine 2',
        types: [
            {
                id: 1,
                title: 'Life Limited Parts',
                description:
                    'Engine Life Limited Parts tracking with cycle limits, remaining life, and back-to-birth documentation',
                contentType: 'llp2-demo',
                apiCategory: 'F008',
            },
        ],
    },
};

// Mock display metadata used exclusively by the IATA binder demo.
// Keyed by apiCategory, or by composite `${folderLetter}:${apiCategory}` when
// the same subfolder code exists under multiple folder letters (e.g. F1/F2).
export const DEMO_SUBFOLDER_METADATA: Record<
    string,
    {
        documentCount: number;
        lastUpdate: string;
        priority: 'high' | 'medium' | 'low';
        progress: number;
    }
> = {
    B004: {
        documentCount: 2,
        lastUpdate: '1 day ago',
        priority: 'high',
        progress: 0,
    },
    C006: {
        documentCount: 24,
        lastUpdate: '2 days ago',
        priority: 'high',
        progress: 42,
    },
    C007: {
        documentCount: 18,
        lastUpdate: '1 day ago',
        priority: 'medium',
        progress: 30,
    },
    H001: {
        documentCount: 6,
        lastUpdate: '20 min ago',
        priority: 'high',
        progress: 55,
    },
    H002: {
        documentCount: 995,
        lastUpdate: '1 hour ago',
        priority: 'high',
        progress: 25,
    },
    'F1:F008': {
        documentCount: 5,
        lastUpdate: '30 mins ago',
        priority: 'high',
        progress: 100,
    },
    'F2:F008': {
        documentCount: 5,
        lastUpdate: '15 mins ago',
        priority: 'high',
        progress: 60,
    },
};

// Demo-specific folder/subfolder enablement (IATA binder demo flow)
const DEMO_ENABLED_SUBFOLDERS: Record<string, Set<string>> = {
    C: new Set(['C006', 'C007']),
    F1: new Set(['F008']),
    F2: new Set(['F008']),
    H: new Set(['H001', 'H002']),
};

export function isDemoSubfolderEnabled(
    folderLetter: string,
    apiCategory: string,
): boolean {
    const enabledSet = DEMO_ENABLED_SUBFOLDERS[folderLetter.toUpperCase()];
    return enabledSet?.has(apiCategory) ?? false;
}

// Asset 360 production folder/subfolder enablement
export const ASSET360_ENABLED_FOLDERS = new Set(['B', 'C', 'H']);

export const ASSET360_ENABLED_SUBFOLDERS: Record<string, Set<string>> = {
    B: new Set(['B004', 'B013']),
    C: new Set(['C006', 'C007']),
    H: new Set(['H001', 'H002']),
};

// Customer-specific IATA folder/subfolder enablement (used with feature flags)
const AP_ENABLED_FOLDERS = new Set(['B', 'C', 'H']);
const AP_ENABLED_SUBFOLDERS: Record<string, Set<string>> = {
    B: new Set(['B004', 'B013']),
    C: new Set(['C006']),
    H: new Set(['H001']),
};

const ACL_ENABLED_FOLDERS = new Set(['B', 'C', 'H']);
const ACL_ENABLED_SUBFOLDERS: Record<string, Set<string>> = {
    B: new Set(['B004', 'B013']),
    C: new Set(['C006']),
    H: new Set(['H001']),
};

export function getEnabledFoldersForCustomer(flags: {
    ap: boolean;
    acl: boolean;
}): { folders: Set<string>; subfolders: Record<string, Set<string>> } | null {
    if (flags.ap)
        return {
            folders: AP_ENABLED_FOLDERS,
            subfolders: AP_ENABLED_SUBFOLDERS,
        };
    if (flags.acl)
        return {
            folders: ACL_ENABLED_FOLDERS,
            subfolders: ACL_ENABLED_SUBFOLDERS,
        };
    return null;
}

// MOCK LDND block, shaped like the EOL-1730 payload (PR #1515). Remove on wire-up.
export const MOCK_LDND_BLOCK: LDNDBlock = {
    refDocIndex: 0,
    actionIndex: 0,
    complianceDocName: 'AAC-038 21HXR21610',
    lastDone: {
        group: 'last_done',
        values: [
            {
                dimension: 'date',
                value: '2025-03-14',
                error: null,
                message: '',
            },
            { dimension: 'fh', value: '27,000.00', error: null, message: '' },
            { dimension: 'fc', value: '23,900', error: null, message: '' },
        ],
        sources: [
            {
                docId: 1234,
                filename: 'DFP 1234',
                previewUrl: null,
                found: true,
            },
        ],
        overdue: false,
    },
    interval: {
        group: 'interval',
        values: [
            { dimension: 'days', value: '180', error: null, message: '' },
            { dimension: 'fh', value: '50,000', error: null, message: '' },
            { dimension: 'fc', value: '40,000', error: null, message: '' },
        ],
        sources: [
            {
                docId: 3456,
                filename: 'AMP 3456',
                previewUrl: null,
                found: true,
            },
        ],
        overdue: false,
    },
    nextDue: {
        group: 'next_due',
        values: [
            {
                dimension: 'date',
                value: '2025-09-10',
                error: null,
                message: '',
            },
            { dimension: 'fh', value: '77,100', error: null, message: '' },
            { dimension: 'fc', value: '63,900', error: null, message: '' },
        ],
        sources: [
            {
                docId: 21,
                filename: 'AD status report 2021-02',
                previewUrl: null,
                found: true,
            },
        ],
        overdue: false,
    },
    aircraftReportStatus: {
        group: 'aircraft_report_status',
        values: [
            {
                dimension: 'date',
                value: '2025-10-20',
                error: null,
                message: '',
            },
            { dimension: 'fh', value: '28,462', error: null, message: '' },
            { dimension: 'fc', value: '24,530', error: null, message: '' },
        ],
        sources: [
            {
                docId: 21,
                filename: 'AD status report 2021-02',
                previewUrl: null,
                found: true,
            },
        ],
        overdue: false,
    },
    remaining: {
        group: 'remaining',
        values: [
            { dimension: 'days', value: '38', error: null, message: '' },
            { dimension: 'fh', value: '420', error: null, message: '' },
            { dimension: 'fc', value: '310', error: null, message: '' },
        ],
        sources: [
            {
                docId: 99,
                filename: 'utilization report 2021-02',
                previewUrl: null,
                found: true,
            },
        ],
        overdue: false,
    },
};
